import { Component, Input, ChangeDetectionStrategy, OnInit, ChangeDetectorRef } from '@angular/core';

import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SimulationResponse, SimulationResults, FluidFlowModelDetailDto } from '../../core/models/simulation.model';
import { getFileNameWithoutExtension } from '../../utils/format.util';
import { RockFilePreviewPopupComponent } from '../popups/rock-file-preview-popup/rock-file-preview-popup.component';
import { RockFilePreviewData } from '../../core/models/rock-file-preview.model';
import { ViewPhasesPopupComponent, ViewPhasesPopupData } from '../popups/view-phases-popup/view-phases-popup.component';
import { SimulatorType } from '../../core/enums/simulator-type.enum';
import { RockModelsService } from '../../services/rock-models/rock-models.service';
import { SimulationsService } from '../../services/simulations/simulations.service';
import { HttpHeaders } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../core/consts/api.const';

@Component({
  selector: 'app-simulation-overview',
  standalone: true,
  imports: [
    MatExpansionModule,
    MatDialogModule
],
  templateUrl: './simulation-overview.component.html',
  styleUrl: './simulation-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SimulationOverviewComponent<T extends SimulationResults> implements OnInit {
  @Input() simulationResponse!: SimulationResponse<T> | null;
  @Input() directions: string[] = ['x', 'y', 'z'];

  // Make utility function available to template
  protected readonly getFileNameWithoutExtension = getFileNameWithoutExtension;
  
  // Rock model name fetched from API
  fetchedRockModelName: string | null = null;
  
  // Fluid flow model name fetched from API
  fetchedFluidFlowModelName: string | null = null;

  // Resolved parent simulation data (used for Kr/N to open rock model / fluid flow previews)
  private resolvedParentSimulationId: string | null = null;
  private resolvedParentFileName: string | null = null;
  private resolvedParentSimulationName: string | null = null;
  private resolvedRockFileModelId: string | null = null;
  private resolvedFluidFlowModelId: string | null = null;
  // The upscaling rock images simulation ID that owns the fluid flow models (for Kr/N)
  private resolvedUpscalingSimulationId: string | null = null;

  constructor(
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly rockModelsService: RockModelsService,
    private readonly simulationsService: SimulationsService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (!this.isUpscalingSimulation()) {
      return;
    }

    const krNTypes = [SimulatorType.UpscalingKr, SimulatorType.UpscalingN];
    const isKrOrN = krNTypes.includes(this.simulationResponse?.type as SimulatorType);

    if (isKrOrN) {
      // Kr/N: rock model and fluid flow model both live on the parent PC simulation.
      // Fetch the parent once and resolve both names together.
      this.loadNamesFromParentSimulation();
    } else {
      // PC/Kabs/FF: parameters are stored directly on the simulation.
      this.loadRockModelNameIfNeeded();
      this.loadFluidFlowModelNameIfNeeded();
    }
  }

  // Used for UpscalingKr / UpscalingN: fetches parent PC simulation once
  // and resolves both rock model name and fluid flow model name from it.
  private loadNamesFromParentSimulation(): void {
    const params = this.simulationResponse?.parameters;
    const parentSimulationId = params?.parentSimulationId;
    if (!parentSimulationId) {
      return;
    }

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: 'simulation-overview',
      }),
    };

    this.simulationsService
      .getSimulationById(parentSimulationId, options)
      .subscribe({
        next: (parentSim: SimulationResponse<SimulationResults>) => {
          // Cache parent data needed for preview navigation
          // Use parentSim.id (the PC simulation's own ID) for fluid flow model API calls
          this.resolvedParentSimulationId = parentSim.id;
          this.resolvedParentFileName = parentSim.fileName;
          this.resolvedParentSimulationName = parentSim.name;
          this.resolvedRockFileModelId = parentSim.parameters?.rockFileModelId ?? null;
          this.resolvedFluidFlowModelId = parentSim.parameters?.fluidFlowModelId ?? null;
          // parentSim.parameters.parentSimulationId = upscaling rock images sim that owns fluid flow models
          this.resolvedUpscalingSimulationId = parentSim.parameters?.parentSimulationId ?? null;

          // Resolve rock model name
          const rockModelId = parentSim.parameters?.rockFileModelId;
          if (rockModelId) {
            if (parentSim.parameters?.rockFileModelName) {
              this.fetchedRockModelName = parentSim.parameters.rockFileModelName;
              this.cdr.markForCheck();
            } else {
              const fileName = parentSim.fileName;
              this.rockModelsService.getUpscalingRockFileModel(fileName, rockModelId, options).subscribe({
                next: (model) => {
                  this.fetchedRockModelName = model.name;
                  this.cdr.markForCheck();
                },
                error: () => {
                  this.fetchedRockModelName = rockModelId;
                  this.cdr.markForCheck();
                },
              });
            }
          }

          // Resolve fluid flow model name
          const fluidFlowModelId = parentSim.parameters?.fluidFlowModelId;
          // upscalingSimId = parentSim.parameters.parentSimulationId = the upscaling rock
          // images simulation that owns the fluid flow models
          const upscalingSimId = parentSim.parameters?.parentSimulationId;
          if (fluidFlowModelId && upscalingSimId) {
            if (parentSim.parameters?.fluidFlowModelName) {
              this.fetchedFluidFlowModelName = parentSim.parameters.fluidFlowModelName;
              this.cdr.markForCheck();
            } else {
              this.fetchFluidFlowModelName(upscalingSimId, fluidFlowModelId, options);
            }
          }
        },
        error: () => {
          // silently ignore — fields simply won't be shown
        },
      });
  }

  private loadRockModelNameIfNeeded(): void {
    if (!this.simulationResponse?.parameters?.rockFileModelId ||
        this.simulationResponse?.parameters?.rockFileModelName) {
      return;
    }

    const fileName = this.simulationResponse.fileName;
    const modelId = this.simulationResponse.parameters.rockFileModelId;
    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: 'simulation-overview',
      }),
    };

    this.rockModelsService.getUpscalingRockFileModel(fileName, modelId, options).subscribe({
      next: (model) => {
        this.fetchedRockModelName = model.name;
        this.cdr.markForCheck();
      },
      error: () => {
        this.fetchedRockModelName = modelId;
        this.cdr.markForCheck();
      },
    });
  }

  private loadFluidFlowModelNameIfNeeded(): void {
    const params = this.simulationResponse?.parameters;
    if (!params?.fluidFlowModelId || !params?.parentSimulationId) {
      return;
    }

    if (params.fluidFlowModelName) {
      this.fetchedFluidFlowModelName = params.fluidFlowModelName;
      this.cdr.markForCheck();
      return;
    }

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: 'simulation-overview',
      }),
    };

    // parentSimulationId = upscaling rock images simulation ID, which owns the fluid flow models
    this.fetchFluidFlowModelName(params.parentSimulationId, params.fluidFlowModelId, options);
  }

  private fetchFluidFlowModelName(
    parentSimulationId: string,
    fluidFlowModelId: string,
    options: { headers: HttpHeaders }
  ): void {
    this.simulationsService.getFluidFlowModelById(parentSimulationId, fluidFlowModelId, options).subscribe({
      next: (model: FluidFlowModelDetailDto) => {
        this.fetchedFluidFlowModelName = model.name;
        this.cdr.markForCheck();
      },
      error: () => {
        this.fetchedFluidFlowModelName = fluidFlowModelId;
        this.cdr.markForCheck();
      },
    });
  }

  get rockModelName(): string | null {
    // First try to get the stored model name
    if (this.simulationResponse?.parameters?.rockFileModelName) {
      return this.simulationResponse.parameters.rockFileModelName;
    }
    // Then try the fetched name
    if (this.fetchedRockModelName) {
      return this.fetchedRockModelName;
    }
    return null;
  }

  get hasRockModel(): boolean {
    if (!this.isUpscalingSimulation()) {
      return false;
    }
    // PC/Kabs/FF store rockFileModelId directly
    if (this.simulationResponse?.parameters?.rockFileModelId) {
      return true;
    }
    // Kr/N derive it from their parent PC simulation
    const krNTypes = [SimulatorType.UpscalingKr, SimulatorType.UpscalingN];
    return (
      krNTypes.includes(this.simulationResponse?.type as SimulatorType) &&
      !!this.simulationResponse?.parameters?.parentSimulationId
    );
  }

  get fluidFlowModelName(): string | null {
    // First try to get the stored model name
    if (this.simulationResponse?.parameters?.fluidFlowModelName) {
      return this.simulationResponse.parameters.fluidFlowModelName;
    }
    // Then try the fetched name
    if (this.fetchedFluidFlowModelName) {
      return this.fetchedFluidFlowModelName;
    }
    return null;
  }

  get hasFluidFlowModel(): boolean {
    if (!this.isUpscalingSimulation()) {
      return false;
    }
    // PC/Kabs/FF store fluidFlowModelId directly
    if (this.simulationResponse?.parameters?.fluidFlowModelId) {
      return true;
    }
    // Kr/N derive it from their parent PC simulation
    const krNTypes = [SimulatorType.UpscalingKr, SimulatorType.UpscalingN];
    return (
      krNTypes.includes(this.simulationResponse?.type as SimulatorType) &&
      !!this.simulationResponse?.parameters?.parentSimulationId
    );
  }

  openRockFilePreview(): void {
    if (!this.simulationResponse?.fileName) return;

    // Check if this is an upscaling simulation
    const isUpscaling = this.isUpscalingSimulation();

    if (isUpscaling) {
      // Open view phases popup for upscaling simulations
      this.openViewPhasesPreview();
    } else {
      // Open rock file preview for subsample simulations
      this.openSubsampleRockFilePreview();
    }
  }

  private isUpscalingSimulation(): boolean {
    if (!this.simulationResponse?.type) return false;
    
    const upscalingTypes = [
      SimulatorType.UpscalingKabs,
      SimulatorType.UpscalingFF,
      SimulatorType.UpscalingPC,
      SimulatorType.UpscalingKr,
      SimulatorType.UpscalingN,
    ];
    
    return upscalingTypes.includes(this.simulationResponse.type as SimulatorType);
  }

  private openSubsampleRockFilePreview(): void {
    if (!this.simulationResponse?.fileName) return;

    const previewData: RockFilePreviewData = {
      fileName: this.simulationResponse.fileName,
    };

    this.dialog.open(RockFilePreviewPopupComponent, {
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'stretch-dialog',
      data: previewData,
    });
  }

  private openViewPhasesPreview(): void {
    if (!this.simulationResponse?.fileName) return;

    const previewData: ViewPhasesPopupData = {
      fileName: this.simulationResponse.fileName,
    };

    this.dialog.open(ViewPhasesPopupComponent, {
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'stretch-dialog',
      data: previewData,
    });
  }

  openRockModelPreview(): void {
    // For Kr/N, rock model data comes from the resolved parent simulation
    const fileName = this.simulationResponse?.parameters?.rockFileModelId
      ? this.simulationResponse.fileName
      : this.resolvedParentFileName;
    const rockFileModelId = this.simulationResponse?.parameters?.rockFileModelId
      ?? this.resolvedRockFileModelId;

    if (!fileName || !rockFileModelId) return;

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/phase-definition'], {
        queryParams: {
          fileName,
          modelId: rockFileModelId,
        },
      })
    );

    window.open(url, '_blank');
  }

  openFluidFlowModelPreview(): void {
    const params = this.simulationResponse?.parameters;
    const isKrOrN = !!this.resolvedParentSimulationId;

    // simulationId for the fluid flow API = the upscaling rock images simulation that owns the models:
    // - For PC: params.parentSimulationId (= upscaling rock images sim ID)
    // - For Kr/N: resolvedUpscalingSimulationId (= parentSim.parameters.parentSimulationId)
    const simulationId = isKrOrN
      ? this.resolvedUpscalingSimulationId
      : (params?.parentSimulationId ?? null);

    const fluidFlowModelId = params?.fluidFlowModelId ?? this.resolvedFluidFlowModelId;
    const rockFile = isKrOrN
      ? (this.resolvedParentFileName ?? '')
      : this.simulationResponse!.fileName;
    const simulationName = isKrOrN
      ? (this.resolvedParentSimulationName ?? this.simulationResponse!.name)
      : this.simulationResponse!.name;
    const rockModelId = params?.rockFileModelId ?? this.resolvedRockFileModelId ?? '';
    const rockModelName = params?.rockFileModelName ?? this.fetchedRockModelName ?? '';

    if (!fluidFlowModelId || !simulationId) return;

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/upscaling/fluid-flow-definition'], {
        queryParams: {
          simulationId,
          simulationName,
          rockFile,
          rockModel: rockModelName,
          rockModelId,
          modelId: fluidFlowModelId,
        },
      })
    );

    window.open(url, '_blank');
  }

}

